'use client';

import geoContract from '@bells/api/geo/frontend';
import { useLanguage } from '@bells/core/i18n';
import { cn } from '@bells/core/lib/utils';
import { generateRestClient } from '@bells/core/rest/react';
import { crudNotFound, locationSearchPlaceholder } from '@bells/i18n';
import type {
  FoundLocation,
  FoundLocationReturn,
  MapSuggestSearchRequest,
} from '@bells/schema/models/mapbox';
import { compact, isEmpty } from 'lodash';
import { Hotel, House, Loader2, MapPin, Search } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDebounce } from 'use-debounce';
import { useMediaQuery } from 'usehooks-ts';
import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from './drawer';
import { FlagIcon } from './flag-icon';
import { Popover, PopoverAnchor, PopoverContent } from './popover';
import { Spinner } from './spinner';

const systemRestClient = generateRestClient(geoContract);

export type LocationSearchInputProps = {
  onLoading?: (val: boolean) => void;
  disabled?: boolean;
  title?: string;
  placeholder?: string;
  onResolve?: (val?: FoundLocationReturn) => void;
  term?: string;
  query?: MapSuggestSearchRequest;
  selectedId?: string;
  className?: string;
};

export const LocationSearchResult = ({
  data,
  isLoading,
  term,
  setAddress,
  selected,
  className,
}: {
  data: FoundLocation[];
  isLoading: boolean;
  term?: string;
  setAddress: (address: FoundLocation) => void;
  selected?: string;
  className?: string;
}) => {
  return (
    <CommandList
      className={`relative h-full max-h-max bg-transparent ${className}`}
    >
      <CommandEmpty>
        {data?.length === 0 && !isLoading && (
          <div className='flex items-start justify-start p-2 text-start text-muted-foreground'>
            {crudNotFound()}
          </div>
        )}
      </CommandEmpty>

      <CommandGroup className={cn('relative h-auto', { hidden: !isLoading })}>
        <div className='relative flex items-center justify-center gap-2 px-2 py-2 uppercase text-muted-foreground'>
          <Loader2 className='h-8 w-8 animate-spin' />
        </div>
      </CommandGroup>
      <CommandGroup className={cn({ hidden: !data || data.length === 0 })}>
        {(data ?? []).map((item, index) => {
          return (
            <CommandItem
              data-active={item.id === selected ? 'true' : 'false'}
              value={item.id}
              className='group cursor-pointer data-[active=true]:!bg-primary data-[active=true]:!text-primary-foreground'
              onSelect={() => {
                setAddress(item);
              }}
              key={`search-${index}`}
              onMouseDown={(e) => e.preventDefault()}
            >
              <div className='flex w-full items-center gap-2'>
                <div className='flex w-full flex-col justify-between gap-1'>
                  <div className='flex w-full flex-1 items-center gap-2 text-sm font-medium'>
                    {item.countryCode && (
                      <FlagIcon
                        countryCode={item.countryCode.toUpperCase()}
                        className='h-4 w-4'
                      ></FlagIcon>
                    )}
                    {item.formatted}
                  </div>

                  {item.address && !isEmpty(term) && (
                    <div className='w-full text-sm text-muted-foreground group-data-[active=true]:text-primary-foreground/80'>
                      {item.address}
                    </div>
                  )}
                </div>

                <div className='flex h-8 w-8 items-center justify-center'>
                  {item.type === 'poi' ? (
                    <div className='flex items-center justify-center overflow-hidden rounded-md bg-blue-500 p-1 text-white'>
                      <Hotel className='size-4' />
                    </div>
                  ) : item.type === 'address' ? (
                    <div className='flex items-center justify-center overflow-hidden rounded-md bg-green-500 p-1 text-white'>
                      <House className='size-4' />
                    </div>
                  ) : (
                    <div className='flex items-center justify-center overflow-hidden rounded-md bg-red-500 p-1 text-white'>
                      <MapPin className='size-4' />
                    </div>
                  )}
                </div>
              </div>
            </CommandItem>
          );
        })}
      </CommandGroup>
    </CommandList>
  );
};

const LocationSearchInput = forwardRef<
  HTMLDivElement,
  LocationSearchInputProps
>(
  (
    {
      disabled,
      onLoading,
      title,
      placeholder,
      query,
      term,
      selectedId: initSelectedId,
      onResolve,
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);

    const placeholderText = useMemo(() => {
      return placeholder ?? locationSearchPlaceholder();
    }, [placeholder]);

    const { language } = useLanguage();
    const [initTerm] = useState<string | undefined>(term);
    const [showInteralTerm, setShowInternalTerm] = useState<string | undefined>(
      initTerm
    );
    const isDesktop = useMediaQuery('(min-width: 768px)');

    const [searchInput, setSearchInput] = useState<string | undefined>(term);
    const [selectedId, setSelectedId] = useState<string | undefined>(
      initSelectedId
    );
    const [debouncedSearchInput] = useDebounce(searchInput, 150);
    const canSearch = useRef<boolean>(false);

    const [data, setData] = useState<FoundLocation[]>([]);
    const [isPendingLocation, setIsPendingLocation] = useState<boolean>(false);
    const [resolvedIsLoading, setResolveIsLoading] = useState<boolean>(false);
    const previousIdRef = useRef<string | undefined>(undefined);

    useEffect(() => {
      if (canSearch.current) {
        loadData(debouncedSearchInput);
      }
    }, [debouncedSearchInput]);

    const loadData = useCallback(
      async (term?: string) => {
        setIsPendingLocation(true);
        setData([]);
        onResolve?.();
        setSelectedId(undefined);
        const result = await systemRestClient.findLocation.mutate({
          body: {
            term: term,
          },
          query: {
            ...query,
            locale: language,
          },
        });

        if (result.status === 200) {
          setData(result.body.items);
        }
        setIsPendingLocation(false);
      },
      [debouncedSearchInput]
    );

    const selectItem = useCallback(
      async (id?: string) => {
        if (!id || id === previousIdRef.current) {
          return;
        }

        previousIdRef.current = id;
        setResolveIsLoading(true);
        const result = await systemRestClient.resolveFindLocation.query({
          params: { id: id },
          query: {
            language,
          },
        });

        if (result.status === 200) {
          onResolve?.(result.body);
        }
        setResolveIsLoading(false);
      },
      [language]
    );

    const loadInit = async () => {
      setIsPendingLocation(true);
      const result = await systemRestClient.findLocation.mutate({
        body: {
          term: debouncedSearchInput,
        },
        query: {
          ...query,
          locale: language,
        },
      });

      if (result.status === 200) {
        setData(result.body.items);
      }
      setIsPendingLocation(false);
      canSearch.current = true;
    };

    useEffect(() => {
      loadInit();
    }, [initTerm]);

    const isLoadingResolve = useMemo(() => {
      return resolvedIsLoading || disabled;
    }, [resolvedIsLoading, disabled]);

    const isLoading = useMemo(() => {
      return isPendingLocation || resolvedIsLoading;
    }, [isPendingLocation, resolvedIsLoading]);

    useEffect(() => {
      onLoading?.(resolvedIsLoading);
    }, [resolvedIsLoading]);

    return isDesktop ? (
      <Popover
        open={open}
        onOpenChange={(val) => {
          if (!isLoadingResolve) {
            setOpen(val);
          }
        }}
      >
        <Command
          ref={ref}
          className='bg-transparent'
          value={selectedId}
          shouldFilter={false}
        >
          <PopoverAnchor asChild>
            <div className='relative w-full rounded-xl border border-input'>
              <CommandInput
                disabled={isLoadingResolve}
                value={showInteralTerm}
                className='bg-transparent font-medium'
                onKeyDown={(e) =>
                  setOpen(!isEmpty(searchInput) && e.key !== 'Escape')
                }
                onMouseDown={() => {
                  setOpen(true);
                }}
                onFocus={() => {
                  setOpen(true);
                }}
                onValueChange={(val) => {
                  setShowInternalTerm(val);
                  setSearchInput(val);
                }}
                placeholder={placeholderText}
              />
              {isLoading && (
                <Spinner className='absolute right-2 top-2 z-[4] size-6'></Spinner>
              )}
            </div>
          </PopoverAnchor>
          {!open && <CommandList aria-hidden='true' className='hidden' />}
          <PopoverContent
            side='bottom'
            align='start'
            style={{
              maxHeight: '50vh',
              overflow: 'auto',
            }}
            avoidCollisions
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              if (
                e.target instanceof Element &&
                e.target.hasAttribute('cmdk-input')
              ) {
                e.preventDefault();
              }
            }}
            className='max-h-[50vh] w-[--radix-popover-trigger-width] overflow-auto p-0'
          >
            <LocationSearchResult
              setAddress={(val) => {
                setSelectedId(val.id);
                selectItem(val.id);
                setOpen(false);

                setShowInternalTerm(
                  compact([val.formatted, val.address]).join(', ')
                );
              }}
              selected={selectedId}
              term={searchInput}
              data={data ?? []}
              isLoading={isPendingLocation}
            ></LocationSearchResult>
          </PopoverContent>
        </Command>
      </Popover>
    ) : (
      <Drawer
        repositionInputs={false}
        open={open}
        onOpenChange={(val) => {
          if (!isLoadingResolve) {
            setOpen(val);
          }
        }}
      >
        <DrawerTrigger asChild>
          <div ref={ref} className='relative w-full'>
            <Button
              type='button'
              disabled={isLoadingResolve}
              variant={'outline'}
              className={cn('w-full items-center justify-start text-left', {
                'text-muted-foreground': !showInteralTerm,
              })}
            >
              <Search className='size-4'></Search>
              <span
                className={cn('w-full flex-1 truncate break-all text-left')}
              >
                {showInteralTerm ?? placeholderText}
              </span>
            </Button>

            {isLoading && (
              <Spinner className='absolute right-2 top-2 z-[4] size-6'></Spinner>
            )}
          </div>
        </DrawerTrigger>
        <DrawerContent className='flex h-[75vh] w-full flex-col'>
          <DrawerHeader>
            <DrawerTitle>{title}</DrawerTitle>
          </DrawerHeader>
          <Command
            shouldFilter={false}
            value={selectedId}
            className='bg-transparent'
          >
            <CommandInput
              value={searchInput}
              disabled={isLoadingResolve}
              className='font-medium'
              onValueChange={(val) => setSearchInput(val)}
              placeholder={placeholderText}
            />
            <LocationSearchResult
              className='px-2'
              setAddress={(val) => {
                setOpen(false);
                setSelectedId(val.id);
                selectItem(val.id);
                setShowInternalTerm(
                  compact([val.formatted, val.address]).join(', ')
                );
              }}
              selected={selectedId}
              term={searchInput}
              data={data ?? []}
              isLoading={isPendingLocation}
            ></LocationSearchResult>
          </Command>
        </DrawerContent>
      </Drawer>
    );
  }
);

LocationSearchInput.displayName = 'LocationSearchInput';
export { LocationSearchInput };
